import { useState } from 'react';
import styled from 'styled-components';

import { simpleDate } from 'lib/date';
import { MappingEntry, MappingType, ReceivedValue } from 'services/api/admin/mappings';
import { colors } from 'styles/theme/colors';
import ChevronDown from 'svg/ChevronDown';

import { Chip, LinkButton, Mono, Muted } from './Chips';
import OperationProblem from './OperationProblem';
import { mappingOpFor, normalizeKey, PendingOperation, percent, slotOf } from './operations';

type Props = {
  type: MappingType;
  fresh: ReceivedValue[];
  entries: Map<string, MappingEntry>;
  operations: PendingOperation[];
  recordNoun: string;
  disabled: boolean;
  onAcknowledge: (entry: MappingEntry) => void;
  onMap: (key: string) => void;
  onDontMap: (key: string, entry: MappingEntry | null) => void;
  onDismiss: (value: ReceivedValue) => void;
  onAccept: (value: ReceivedValue) => void;
  onReject: (value: ReceivedValue) => void;
  onUndo: (slot: string) => void;
  onKeepMine: (op: PendingOperation) => void;
};

export default function NewValues({
  type,
  fresh,
  entries,
  operations,
  recordNoun,
  disabled,
  onAcknowledge,
  onMap,
  onDontMap,
  onDismiss,
  onAccept,
  onReject,
  onUndo,
  onKeepMine,
}: Props) {
  const [open, setOpen] = useState(fresh.length > 0);
  const canDontMap = type !== 'group_types';

  const meta = (value: ReceivedValue) =>
    [
      `${value.recordCount} ${recordNoun}${value.recordCount === 1 ? '' : 's'}`,
      `first ${simpleDate(value.firstSeenAt)}`,
      value.lastSeenAt ? `last ${simpleDate(value.lastSeenAt)}` : null,
    ]
      .filter(Boolean)
      .join(' · ');

  const pendingFor = (value: ReceivedValue) => {
    const dismissOp = operations.find((o) => 'attrValueId' in o && o.attrValueId === value.id);
    return dismissOp ?? mappingOpFor(operations, type, value.name);
  };

  const renderRow = (value: ReceivedValue) => {
    const entry = entries.get(normalizeKey(value.name)) ?? null;
    const agentMapped = value.needsReview && entry?.setBy === 'agent';
    const suggestion = value.status === 'suggested' ? value.suggestion : null;
    const pending = pendingFor(value);

    return (
      <Row key={value.id}>
        <RowMain>
          <Mono title={value.name}>{value.name}</Mono>
          <Status>
            {suggestion ? (
              <>
                Agent suggests <strong>{suggestion.target?.name ?? 'an Olio record'}</strong>
                {percent(suggestion.confidence) && ` (${percent(suggestion.confidence)})`}
              </>
            ) : agentMapped ? (
              <>
                Agent mapped it to <strong>{entry?.resolved?.name ?? 'an Olio record'}</strong>
                {percent(entry?.confidence ?? entry?.agentConfidence) &&
                  ` (${percent(entry?.confidence ?? entry?.agentConfidence)})`}
              </>
            ) : (
              'Unmapped, no agent mapping'
            )}
          </Status>
          <Muted>{meta(value)}</Muted>
          <OperationProblem op={pending} onKeepMine={onKeepMine} onUndo={onUndo} />
        </RowMain>
        <Actions>
          {pending ? (
            <>
              <Chip $tone='amber'>Pending {describe(pending)}</Chip>
              <LinkButton onClick={() => onUndo(slotOf(pending))}>Undo</LinkButton>
            </>
          ) : disabled ? null : suggestion ? (
            <>
              <LinkButton onClick={() => onAccept(value)}>Accept</LinkButton>
              <LinkButton onClick={() => onReject(value)}>Reject</LinkButton>
              <LinkButton onClick={() => onMap(value.name)}>Map</LinkButton>
            </>
          ) : agentMapped && entry ? (
            <>
              <LinkButton onClick={() => onAcknowledge(entry)}>Acknowledge</LinkButton>
              <LinkButton onClick={() => onMap(value.name)}>Change</LinkButton>
              {canDontMap && <LinkButton onClick={() => onDontMap(value.name, entry)}>Don&apos;t map</LinkButton>}
            </>
          ) : (
            <>
              <LinkButton onClick={() => onMap(value.name)}>Map</LinkButton>
              {canDontMap && <LinkButton onClick={() => onDontMap(value.name, entry)}>Don&apos;t map</LinkButton>}
              <LinkButton onClick={() => onDismiss(value)}>Dismiss</LinkButton>
            </>
          )}
        </Actions>
      </Row>
    );
  };

  return (
    <Area>
      <AreaHeader onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span>New values {fresh.length > 0 && <Chip $tone='red'>{fresh.length}</Chip>}</span>
        <Chevron $open={open} color={colors.black50} />
      </AreaHeader>
      {open && (
        <AreaBody>
          {fresh.length === 0 ? (
            <Muted>Nothing new. Every received value is mapped or reviewed.</Muted>
          ) : (
            fresh.map(renderRow)
          )}
        </AreaBody>
      )}
    </Area>
  );
}

function describe(op: PendingOperation) {
  switch (op.op) {
    case 'set':
      return `→ ${op.label ?? 'new mapping'}`;
    case 'acknowledge':
      return 'acknowledge';
    case 'dont_map':
      return "don't map";
    case 'remove':
      return 'remove';
    case 'dismiss':
      return 'dismiss';
    case 'accept_suggestion':
      return `accept → ${op.label ?? 'suggestion'}`;
    case 'reject_suggestion':
      return 'reject suggestion';
    default:
      return '';
  }
}

const Area = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 6px;
  background: ${colors.white};
`;

const AreaHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
`;

const Chevron = styled(ChevronDown)<{ $open: boolean }>`
  flex-shrink: 0;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '180deg')});
  transform-origin: center;
`;

const AreaBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px 12px;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px 0;
  border-top: 1px solid ${colors.black5};
`;

const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Status = styled.span`
  font-size: 13px;

  strong {
    font-weight: 500;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-shrink: 0;
`;
